import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { dismissToast, markItemsRead, selectToasts } from "store/notificationsSlice";
import { notificationsService } from "api/notifications";
import { INotificationItem } from "api/notifications/notifications.type";
import { getNotificationRoute, getNotificationDownloadUrl, isToastForOpenDraft } from "components/WhatsNewMenu/notificationDisplay";
import { PATHS } from "router/config/paths";
import { cn } from "utils/cn";
import { downloadFile } from "utils/downloadFile";
import { useSnackbar } from "hooks/useSnackbar";
import ToastCard from "./ToastCard";
import { BUTTON_SECONDARY } from "components/Button/sleekButton";

// Global top-right stacking toaster for generation-completion notifications (REF-6306). Mounted once
// in pages/Layout/Layout.tsx. Reads the transient `toasts` queue (store/notificationsSlice) that
// SocketListener pushes into whenever a WS frame's `notification.surface` is truthy.
//
// Dismissal policy: a toast persists until the user closes it with its "x" — EXCEPT when its target
// route is the page the user is ALREADY on. In that case the toast is redundant (the result is right
// there), so it auto-dismisses after AUTO_DISMISS_MS. Hovering the deck pauses those timers so a toast
// never vanishes mid-read. Off-page (and download) toasts never auto-dismiss.
//
// Collapsed (default): toasts overlap into a "deck" — newest on top, older ones falling back with a
// vertical offset + scale/opacity falloff, capped at VISIBLE_STACK. Hovering fans them into a vertical
// list plus a "See all" link. No framer-motion (not a dependency) — enter/exit is CSS transitions;
// a departing toast is held in `exitingIds` for one tick before dismissToast so the deck doesn't snap.
const VISIBLE_STACK = 3;
const AUTO_DISMISS_MS = 6000;
const EXIT_TRANSITION_MS = 220;

const NotificationToaster = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { showError } = useSnackbar();
  const toasts = useAppSelector(selectToasts);

  const [expanded, setExpanded] = useState(false);
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());

  // Auto-dismiss bookkeeping — only same-page toasts get an entry. `timeoutId` is null while paused
  // (hovered); `remaining`/`startedAt` let hover-leave resume with the time actually left.
  const timers = useRef<Map<string, { timeoutId: ReturnType<typeof setTimeout> | null; remaining: number; startedAt: number }>>(new Map());

  // A toast is "on the current page" when its route resolves to the pathname the user is already on.
  // Download toasts (no route) and off-page toasts return false → they persist until dismissed.
  const isOnCurrentPage = (item: INotificationItem): boolean => {
    const route = getNotificationRoute(item);
    if (!route) return false;
    const [pathname, query = ""] = route.split("?");
    if (pathname !== location.pathname) return false;
    // REF-6607: one pathname serves many canvases — a toast for ANOTHER canvas (e.g. a comment
    // mention) is not "this page" and must not auto-dismiss under the user.
    const targetId = new URLSearchParams(query).get("id");

    return !targetId || targetId === new URLSearchParams(location.search).get("id");
  };

  // Animate out (one transition tick) before actually removing the toast from the queue.
  const requestDismiss = (id: string) => {
    const timer = timers.current.get(id);
    if (timer?.timeoutId) clearTimeout(timer.timeoutId);
    timers.current.delete(id);

    setExitingIds((prev) => new Set(prev).add(id));
    setTimeout(() => {
      dispatch(dismissToast(id));
      setExitingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);

        return next;
      });
    }, EXIT_TRANSITION_MS);
  };

  // Schedule a 6s auto-dismiss for every NEW same-page toast; drop bookkeeping for anything no longer
  // in the queue. Off-page toasts never get a timer, so they persist.
  useEffect(() => {
    const liveIds = new Set(toasts.map((item) => item.id));

    toasts.forEach((item) => {
      // The analysis of the draft on screen finished / failed — the screen already shows it; the bell keeps the entry.
      if (isToastForOpenDraft(item, location.search, location.pathname)) {
        dispatch(dismissToast(item.id));

        return;
      }
      if (timers.current.has(item.id) || exitingIds.has(item.id) || !isOnCurrentPage(item)) return;

      if (expanded) {
        timers.current.set(item.id, { timeoutId: null, remaining: AUTO_DISMISS_MS, startedAt: Date.now() });
      } else {
        const timeoutId = setTimeout(() => requestDismiss(item.id), AUTO_DISMISS_MS);
        timers.current.set(item.id, { timeoutId, remaining: AUTO_DISMISS_MS, startedAt: Date.now() });
      }
    });

    timers.current.forEach((timer, id) => {
      if (!liveIds.has(id)) {
        if (timer.timeoutId) clearTimeout(timer.timeoutId);
        timers.current.delete(id);
      }
    });
  }, [toasts]);

  // Pause auto-dismiss timers on hover, resume with the remaining time on leave.
  useEffect(() => {
    if (expanded) {
      timers.current.forEach((timer, id) => {
        if (timer.timeoutId) {
          clearTimeout(timer.timeoutId);
          const remaining = Math.max(0, timer.remaining - (Date.now() - timer.startedAt));
          timers.current.set(id, { timeoutId: null, remaining, startedAt: Date.now() });
        }
      });
    } else {
      timers.current.forEach((timer, id) => {
        if (!timer.timeoutId) {
          const timeoutId = setTimeout(() => requestDismiss(id), timer.remaining);
          timers.current.set(id, { timeoutId, remaining: timer.remaining, startedAt: Date.now() });
        }
      });
    }
  }, [expanded]);

  // Clean up every pending timer on unmount.
  useEffect(
    () => () => {
      timers.current.forEach((timer) => {
        if (timer.timeoutId) clearTimeout(timer.timeoutId);
      });
      timers.current.clear();
    },
    []
  );

  // REF-6306 — cross-tab toast sync: each tab has its own WS + its own toast queue, so a completion
  // pops a toast in every open tab. When the user dismisses or clicks one, mirror that to the other tabs
  // via BroadcastChannel so the same toast doesn't linger elsewhere (a clicked toast also marks read).
  // Only MANUAL dismiss/click broadcasts — the same-page 6s auto-dismiss stays per-tab. The receiver
  // calls requestDismiss (never re-broadcasts), so there's no echo loop. `dispatch`/`requestDismiss`
  // read only stable setters + refs, so capturing them once here is safe.
  const toastChannelRef = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;

    const channel = new BroadcastChannel("refabric-toast-sync");
    toastChannelRef.current = channel;
    channel.onmessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; id?: string; read?: boolean } | null;
      if (!data || data.type !== "dismiss" || !data.id) return;
      if (data.read) dispatch(markItemsRead([data.id]));
      requestDismiss(data.id);
    };

    return () => {
      channel.close();
      toastChannelRef.current = null;
    };
  }, []);

  const broadcastDismiss = (id: string, read: boolean) => {
    toastChannelRef.current?.postMessage({ type: "dismiss", id, read });
  };

  const handleCardClick = (id: string) => {
    const item = toasts.find((toast) => toast.id === id);

    // Clicking a toast marks its notification read (optimistic; matches NotificationItem's row click).
    if (item && item.read_at == null) {
      dispatch(markItemsRead([id]));
      notificationsService.markRead([id]).catch(() => undefined);
    }

    // Mirror the dismiss + read to any other open tab (see the BroadcastChannel effect above).
    broadcastDismiss(id, true);

    // REF-6306 — a download-kind toast with a download_url downloads the file instead of navigating
    // (there is no route for it either way — see getNotificationRoute's `download` case).
    const downloadUrl = item ? getNotificationDownloadUrl(item) : null;
    if (downloadUrl) {
      downloadFile(downloadUrl).catch(() => showError(t("notifications:download.failed")));
      requestDismiss(id);

      return;
    }

    const route = item ? getNotificationRoute(item) : null;

    if (route) {
      // A route to a different page opens in a new tab so the toast doesn't yank the user off
      // whatever they're currently doing; a route to the current page navigates in place.
      const routePathname = route.split("?")[0];
      if (routePathname !== location.pathname) window.open(route, "_blank", "noopener,noreferrer");
      else navigate(route, { state: { fromNotification: id } });
    }

    requestDismiss(id);
  };

  const handleSeeAll = () => {
    if (PATHS.NOTIFICATIONS !== location.pathname) window.open(PATHS.NOTIFICATIONS, "_blank", "noopener,noreferrer");
    else navigate(PATHS.NOTIFICATIONS);
  };

  if (toasts.length === 0) return null;

  const stack = toasts.slice(0, VISIBLE_STACK);
  const overflowCount = Math.max(0, toasts.length - VISIBLE_STACK);
  const deckHeight = 76 + (Math.min(stack.length, VISIBLE_STACK) - 1) * 10;

  return (
    <div className="fixed z-[100000] w-80 max-w-[calc(100vw-2rem)]" style={{ top: "1.5rem", right: "1.5rem" }} onMouseEnter={() => setExpanded(true)} onMouseLeave={() => setExpanded(false)}>
      <div className="relative transition-[height] duration-300 ease-out" style={{ height: expanded ? undefined : deckHeight }}>
        {stack.map((item, index) => (
          <div
            key={item.id}
            className={cn("transition-all duration-300 ease-out", expanded ? "relative mb-2 last:mb-0" : "absolute inset-x-0 top-0")}
            style={
              expanded
                ? { zIndex: VISIBLE_STACK - index }
                : {
                    transform: `translateY(${index * 10}px) scale(${1 - index * 0.03})`,
                    opacity: 1 - index * 0.18,
                    zIndex: VISIBLE_STACK - index,
                    pointerEvents: index === 0 ? "auto" : "none",
                  }
            }
          >
            <ToastCard
              item={item}
              exiting={exitingIds.has(item.id)}
              onDismiss={() => {
                broadcastDismiss(item.id, false);
                requestDismiss(item.id);
              }}
              onClick={() => handleCardClick(item.id)}
            />
          </div>
        ))}
      </div>

      {expanded && overflowCount > 0 && (
        <button type="button" onClick={handleSeeAll} className={cn("mt-1 w-full rounded-full py-2 text-bodySm font-medium text-foreground", BUTTON_SECONDARY)}>
          {t("notifications:toast.more", { count: overflowCount })} · {t("notifications:toast.see_all")}
        </button>
      )}
    </div>
  );
};

export default NotificationToaster;
